import countriesData from '../locales/countries.json';
import countryDefaultCurrency from '../data/countryDefaultCurrency.json';
import { Country } from '../types';

export type CountryCode = (typeof countriesData)[number]['id'];

export const COUNTRY_CODES: CountryCode[] = countriesData.map((country) => country.id);

/** ISO codes that have at least one bank in functions/src/banks.json */
export const BANK_COUNTRY_CODES: CountryCode[] = ['AE', 'EG', 'GB', 'SA', 'OTHER'];

export const COUNTRY_DEFAULT_CURRENCY: Record<string, string> = countryDefaultCurrency;

/** Regional-indicator emoji flag from an ISO 3166-1 alpha-2 code. */
export function countryCodeToFlag(code: string): string {
  if (code === 'OTHER') return '🌍';
  const upper = code.toUpperCase();
  if (upper.length !== 2) return '🌍';
  return String.fromCodePoint(
    ...upper.split('').map((char) => 127397 + char.charCodeAt(0))
  );
}

export function getCountryFlag(code: string): string {
  const entry = countriesData.find(
    (country) => country.id.toUpperCase() === code.toUpperCase()
  );
  return entry?.flag ?? countryCodeToFlag(code);
}

export function getCountryDefaultCurrency(code: string): string {
  return COUNTRY_DEFAULT_CURRENCY[code] ?? COUNTRY_DEFAULT_CURRENCY.OTHER ?? 'USD';
}

type TranslateFn = (key: string, options?: { ns?: string }) => string;

/** Resolve a translated country name; falls back to the code when missing. */
export function getCountryName(code: string, t: TranslateFn): string {
  const translated = t(`countries.${code}`, { ns: 'common' });
  return translated.startsWith('countries.') ? code : translated;
}

/** Build translated country options sorted alphabetically, with OTHER last. */
export function getTranslatedCountries(t: TranslateFn): Country[] {
  const translated = countriesData.map((country) => ({
    id: country.id,
    flag: country.flag,
    name: getCountryName(country.id, t),
  }));

  const regular = translated
    .filter((country) => country.id !== 'OTHER')
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const other = translated.find((country) => country.id === 'OTHER');
  return other ? [...regular, other] : regular;
}

/** Countries where Nimah has a bank list (subset of getTranslatedCountries). */
export function getTranslatedBankCountries(t: TranslateFn): Country[] {
  const allowed = new Set<string>(BANK_COUNTRY_CODES);
  return getTranslatedCountries(t).filter((country) => allowed.has(country.id));
}

/** Pick a valid bank-country default, or empty when none applies. */
export function getDefaultBankCountry(primaryCountry: string): string {
  return BANK_COUNTRY_CODES.includes(primaryCountry as CountryCode) ? primaryCountry : '';
}
